import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { PROJECTS_DATA_QUERY_KEYS } from '@/entities/projects-data/model/projectsDataQueryKeys';

import { ApiCexPairs, ApiPairs } from 'api';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedDispatch } from 'store';
import { deletePairFromProject, updatePairInProject } from 'store/slices/projects';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { EExchange } from 'web3';

interface IUseUdPairModalProps {
  onClose: () => void;
  onOpen: () => void;
  pairId: number;
  exchange: EExchange;
  projectId: number;
  pairSymbol: string;
  initialNotes: string;
}

const validationSchema = Yup.object({ notes: Yup.string().max(100, 'Notes field is too long') });

const useUDPairModal = ({
  onClose,
  onOpen,
  pairId,
  exchange,
  projectId,
  pairSymbol,
  initialNotes,
}: IUseUdPairModalProps) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);

  const initialValues = useMemo(() => ({ notes: initialNotes ?? '' }), [initialNotes]);

  const onUpdatePair = useCallback(
    async ({ notes }: { notes: string }) => {
      setLoading(true);

      try {
        const { isSuccess, errorMessage } =
          exchange === EExchange.dex
            ? await ApiPairs.updateDexPair({ id: pairId, notes })
            : await ApiCexPairs.updateCexPair({ id: pairId, notes });

        if (!isSuccess) {
          onClose();
          setLoading(false);
          dispatch(
            setAlertState({
              type: 'failed',
              text: errorMessage,
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
                onOpen();
              },
            }),
          );
          return;
        }

        if (isSuccess) {
          const { isSuccess, errorMessage } =
            exchange === EExchange.dex
              ? await ApiPairs.getDexPairById(pairId)
              : await ApiCexPairs.getCexPairById(pairId);

          onClose();
          setLoading(false);

          if (!isSuccess) {
            dispatch(
              setAlertState({
                type: 'failed',
                text: errorMessage,
                onClose: () => dispatch(dropAlertState()),
                onSubmit: () => {
                  dispatch(dropAlertState());
                  onOpen();
                },
              }),
            );
            return;
          }

          if (isSuccess) {
            dispatch(updatePairInProject({ notes, pairId, projectId, type: exchange }));
            queryClient.invalidateQueries({ queryKey: PROJECTS_DATA_QUERY_KEYS.all });
            dispatch(
              setAlertState({
                type: 'success',
                text: `Updated traiding pair: ${pairSymbol}`,
                onClose: () => dispatch(dropAlertState()),
                onSubmit: () => dispatch(dropAlertState()),
              }),
            );
            return;
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [pairId, pairSymbol, onClose, onOpen, dispatch, exchange, projectId, queryClient],
  );

  const onDeletePair = useCallback(async () => {
    try {
      setLoading(true);

      const { isSuccess, errorMessage } =
        exchange === EExchange.dex
          ? await ApiPairs.deleteDexPair(pairId)
          : await ApiCexPairs.deleteCexPair(pairId);

      onClose();
      setLoading(false);

      if (!isSuccess) {
        dispatch(
          setAlertState({
            type: 'failed',
            text: errorMessage,
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
              onOpen();
            },
          }),
        );
        return;
      }

      if (isSuccess) {
        navigate(generatePath(ROUTE_PATHS.project, { id: projectId.toString() }));
        dispatch(deletePairFromProject({ pairId, projectId, type: exchange }));
        queryClient.invalidateQueries({ queryKey: PROJECTS_DATA_QUERY_KEYS.all });
        dispatch(
          setAlertState({
            type: 'success',
            text: `Deleted pair: ${pairSymbol}`,
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
        return;
      }
    } catch (error) {
      console.log(error);
    }
  }, [pairId, onOpen, onClose, projectId, dispatch, exchange, navigate, pairSymbol, queryClient]);

  const { values, handleSubmit, setFieldValue, errors, touched } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: onUpdatePair,
    enableReinitialize: true,
  });

  return { loading, onDeletePair, values, errors, touched, handleSubmit, setFieldValue };
};

export { useUDPairModal };
